import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, of } from 'rxjs';
import { MediaKitService } from '@/app/services/media-kit/media-kit.service';
import { MediaKitConfigDto } from '@/app/common/general-types/media-kit.dto';
import { ToggleComponent } from '@/app/atoms/form-components/toggle/toggle.component';
import { FilterItemDto } from '@/app/shared/models/misc';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';

@Component({
  selector: 'ins-creator-media-kit',
  imports: [ToggleComponent],
  templateUrl: './media-kit.html',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorMediaKit implements OnInit {
  readonly creatorUserId = signal<string | undefined>(undefined);
  readonly mediaKitConfig = signal<MediaKitConfigDto | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  readonly showHideOptions: FilterItemDto[] = [
    { id: 1, name: 'Show', value: true },
    { id: 2, name: 'Hide', value: false },
  ];

  selectedBioOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedAgeOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedAddressOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedInterestsOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedReviewsOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedVideosOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedStatsOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedStatsInstagramOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedStatsTiktokOption = signal<FilterItemDto>(this.showHideOptions[0]);
  selectedStatsYoutubeOption = signal<FilterItemDto>(this.showHideOptions[0]);

  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private mediaKitService = inject(MediaKitService);
  private toastNotificationService = inject(ToastNotificationService);

  ngOnInit(): void {
    const creatorId = this.route.parent?.snapshot.paramMap.get('creatorId') ?? undefined;
    this.creatorUserId.set(creatorId);
    if (creatorId) {
      this.loadConfig(creatorId);
    }
  }

  onToggle(field: keyof MediaKitConfigDto, option: FilterItemDto): void {
    const config = this.mediaKitConfig();
    if (!config?.id) {
      return;
    }

    if (
      (field === 'show_stats_instagram' ||
        field === 'show_stats_tiktok' ||
        field === 'show_stats_youtube') &&
      !this.selectedStatsOption().value
    ) {
      return;
    }

    const nextValue = !!option.value;
    const prevValue = !!config[field];
    const prevOption = this.optionFromBool(prevValue);

    this.mediaKitConfig.update((c) => (c ? { ...c, [field]: nextValue } : c));
    this.setSelectedOption(field, option);

    this.mediaKitService.updateMediaKitConfig(config.id, { [field]: nextValue }).subscribe({
      next: (updated) => {
        this.mediaKitConfig.set(updated);
        this.hydrateSelections(updated);
      },
      error: () => {
        this.mediaKitConfig.update((c) => (c ? { ...c, [field]: prevValue } : c));
        this.setSelectedOption(field, prevOption);
        void this.toastNotificationService.alert({
          title: 'Update failed',
          message: 'Could not update media kit setting.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  private loadConfig(creatorId: string): void {
    this.loadingState.set('loading');
    this.mediaKitService
      .getMediaKitConfigByCreatorId(creatorId)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((config) => {
        if (config) {
          this.mediaKitConfig.set(config);
          this.hydrateSelections(config);
          this.loadingState.set('loaded');
        }
      });
  }

  private hydrateSelections(config: MediaKitConfigDto): void {
    this.selectedBioOption.set(this.optionFromBool(config.show_bio));
    this.selectedAgeOption.set(this.optionFromBool(config.show_age));
    this.selectedAddressOption.set(this.optionFromBool(config.show_address));
    this.selectedInterestsOption.set(this.optionFromBool(config.show_interests));
    this.selectedReviewsOption.set(this.optionFromBool(config.show_reviews));
    this.selectedVideosOption.set(this.optionFromBool(config.show_videos));
    this.selectedStatsOption.set(this.optionFromBool(config.show_stats));
    this.selectedStatsInstagramOption.set(this.optionFromBool(config.show_stats_instagram));
    this.selectedStatsTiktokOption.set(this.optionFromBool(config.show_stats_tiktok));
    this.selectedStatsYoutubeOption.set(this.optionFromBool(config.show_stats_youtube));
  }

  private optionFromBool(value: boolean): FilterItemDto {
    return value ? this.showHideOptions[0] : this.showHideOptions[1];
  }

  private setSelectedOption(field: keyof MediaKitConfigDto, option: FilterItemDto): void {
    switch (field) {
      case 'show_bio':
        this.selectedBioOption.set(option);
        break;
      case 'show_age':
        this.selectedAgeOption.set(option);
        break;
      case 'show_address':
        this.selectedAddressOption.set(option);
        break;
      case 'show_interests':
        this.selectedInterestsOption.set(option);
        break;
      case 'show_reviews':
        this.selectedReviewsOption.set(option);
        break;
      case 'show_videos':
        this.selectedVideosOption.set(option);
        break;
      case 'show_stats':
        this.selectedStatsOption.set(option);
        break;
      case 'show_stats_instagram':
        this.selectedStatsInstagramOption.set(option);
        break;
      case 'show_stats_tiktok':
        this.selectedStatsTiktokOption.set(option);
        break;
      case 'show_stats_youtube':
        this.selectedStatsYoutubeOption.set(option);
        break;
    }
  }
}
